import * as React from "react";
import styled from "@emotion/styled";
import classNames from "classnames";
import Title from "@/components/atoms/Title";
import FormBlock from "@/components/molecules/FormBlock";
import FormContainer from "@/components/atoms/FormContainer";
import PropertyType from "@/constants/PropertyType";
import FormContext, { initFormStore } from "@/contexts/Form";
import form from "@/utils/form";
import NextButton from "@/components/molecules/NextButton";
import CapsuleNextButton from "@/components/molecules/CapsuleNextButton";
import Article from "@/components/molecules/Article";
import MiniTitle from "@/components/atoms/MiniTitle";
import { useFormHandler } from "@/utils/hooks";
// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import contract, {
  HIDDEN_COMPONENT_OPTIONS,
  isHiddenComponent,
  isReasonForSaleComponentHidden,
  Feature,
} from "@/utils/contract";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import { reasonForSaleTypeOptions } from "@/constants/ReasonForSale";
import { inquiryValuation as validator } from "@/utils/validation";
import { useTheme } from "@/hooks/useTheme";

const SALE_PERIOD_VALUES = ["早急に", "3ヶ月以内", "半年以内", "1年以内", "1年以上先"];

const SALE_REASON_VALUES = ["名義人", "名義人の家族、親族", "共有名義", "会社名義", "名義人から依頼を受けた代理人"];

const SALE_REASON_VALUES_HOUSE = ["土地のみ名義人", "建物のみ名義人"];

type SalePeriodBlockProps = {
  onNext: () => void;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

const IntersectionObserverContainer = styled.div`
  opacity: 0;

  &.isVisible {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;

const SaleReasonBlock = ({ onNext }: SalePeriodBlockProps) => {
  const { COLOR_THEME, isNewTheme } = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const {
    state: { values, validates },
    isValidProperty,
    isValidPropertyDetails,
    isRequiredReason,
  } = React.useContext(FormContext) || initFormStore(form);

  const {
    state: { featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);
  const [showNextButton, setShowNextButton] = React.useState(true);
  const hiddenPurchaseIntentionComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.PURCHASE_INTENTION);
  const hiddenReasonForSaleComponent = isReasonForSaleComponentHidden(featuresObj);

  const handleSaleReason = useFormHandler("saleReason", validator.saleReason);
  const handleSalePeriod = useFormHandler("salePeriod");
  const handleRelation = useFormHandler("relation");

  if (typeof IntersectionObserver !== "undefined") {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ isIntersecting }) => setIsVisible(isIntersecting));
    });

    React.useEffect(() => {
      if (ref.current) {
        observer.observe(ref.current);
      }
      return () => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      };
    });
  }

  const firstRef = React.useRef(true);

  /**
   * 次へボタンクリック イベント
   */
  const handleNext = () => {
    // 次の入力エリアを表示する
    firstRef.current = false;
    onNext();
  };

  /**
   * コンポーネント非表示のfeature監視
   */
  React.useEffect(() => {
    // featureが設定済みの場合、
    // 入力オペレーションをスキップして次の入力エリアを表示する
    if (hiddenPurchaseIntentionComponent) {
      onNext();
    }
  }, [hiddenPurchaseIntentionComponent]);
  const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;
  const isConnectingRoadInfoAutoEnabled = featuresObj[Feature.CONNECTING_ROAD_INFO_AUTO]?.enabled;

  const isValid =
    isValidProperty(isTrafficInfoAutoLinked) &&
    isValidPropertyDetails(isConnectingRoadInfoAutoEnabled) &&
    isRequiredReason(hiddenPurchaseIntentionComponent, hiddenReasonForSaleComponent);

  const renderMiniTitle = () => {
    return <Title color={COLOR_THEME.extras?.secondary}>ご検討状況をお聞かせください</Title>;
  };

  const renderNextButton = () => {
    return (
      <React.Fragment>
        {!isNewTheme ? (
          <NextButton disabled={firstRef.current && !isValid} onClick={handleNext} value="もう少しで完了します" />
        ) : (
          <CapsuleNextButton
            disabled={firstRef.current && !isValid}
            value="もう少しで完了です"
            minWidth="260px"
            className="formBtn"
            onClick={() => {
              setShowNextButton(false);
              handleNext();
            }}
          />
        )}
      </React.Fragment>
    );
  };

  const formBlockProps = {
    className: `${isNewTheme ? "form" : ""}`,
    backgroundColor: "#ffffff",
  };

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  return (
    <>
      {!hiddenPurchaseIntentionComponent && (
        <IntersectionObserverContainer className={classNames({ isVisible })} ref={ref}>
          <Article>
            {isNewTheme ? (
              renderMiniTitle()
            ) : (
              <Article.Header>
                <MiniTitle>{isNewTheme ? "ご検討状況をお聞かせください" : "売却理由をお聞かせください"}</MiniTitle>
              </Article.Header>
            )}
            <Article.Block className={isNewTheme ? "border-none bgWhite" : ""}>
              {hiddenReasonForSaleComponent && (
                <FormContainer className={isCssOverride}>
                  <FormBlock {...formBlockProps}>
                    <FormBlock.Label
                      value="売却理由"
                      required
                      background={COLOR_THEME.extras?.required_icon?.background}
                      text={COLOR_THEME.extras?.required_icon?.text}
                      border={COLOR_THEME.extras?.required_icon?.border}
                    />
                    <FormBlock.Row>
                      <FormBlock.Select
                        options={reasonForSaleTypeOptions}
                        value={values.saleReason}
                        onChange={ev => handleSaleReason(ev.target.value)}
                        aria-label="売却理由"
                        width={isNewTheme ? "400px" : "70%"}
                        validateMessage={validates.saleReason}
                        background={COLOR_THEME.extras?.invalid_state?.background}
                        required
                      />
                    </FormBlock.Row>
                  </FormBlock>
                </FormContainer>
              )}
              <FormContainer className={isCssOverride}>
                <FormBlock {...formBlockProps}>
                  <FormBlock.Label
                    value={isNewTheme ? "売却をご希望の時期" : "売却希望時期"}
                    required
                    background={COLOR_THEME.extras?.required_icon?.background}
                    text={COLOR_THEME.extras?.required_icon?.text}
                    border={COLOR_THEME.extras?.required_icon?.border}
                  />
                  <FormBlock.Row>
                    <FormBlock.Radio
                      name="saleperiod"
                      value={values.salePeriod}
                      values={SALE_PERIOD_VALUES}
                      onChangeRadio={value => handleSalePeriod(value)}
                      aria-label="売却希望時期"
                    />
                  </FormBlock.Row>
                </FormBlock>
              </FormContainer>
              <FormContainer className={isCssOverride}>
                <FormBlock {...formBlockProps}>
                  <FormBlock.Label
                    value={isNewTheme ? "査定物件とのご関係" : "あなたと査定物件の関係"}
                    required
                    background={COLOR_THEME.extras?.required_icon?.background}
                    text={COLOR_THEME.extras?.required_icon?.text}
                    border={COLOR_THEME.extras?.required_icon?.border}
                  />
                  <FormBlock.Row>
                    <FormBlock.Radio
                      name="relation"
                      value={values.relation}
                      values={
                        PropertyType.HOUSE === values.propertyType
                          ? [...SALE_REASON_VALUES, ...SALE_REASON_VALUES_HOUSE]
                          : [...SALE_REASON_VALUES]
                      }
                      onChangeRadio={value => handleRelation(value)}
                      aria-label="あなたと査定物件の関係"
                    />
                  </FormBlock.Row>
                </FormBlock>
              </FormContainer>
            </Article.Block>
            {showNextButton && renderNextButton()}
          </Article>
        </IntersectionObserverContainer>
      )}
    </>
  );
};

export default SaleReasonBlock;
